import React, { memo } from 'react';
import { ScrollView, TouchableOpacity, Text, StyleSheet, View } from 'react-native';
import type { CorkBoardTag } from '@/types/residents/corkBoard';
import { TagBadge } from './TagBadge';
import { useThemedStyles } from '@/hooks/residents/useThemeColors';
import type { ThemeContextValue } from '@/hooks/residents/useThemeColors';

interface TagFilterBarProps {
  tags: CorkBoardTag[];
  selectedTagIds: number[];
  onToggleTag: (tagId: number) => void;
  onClearFilters: () => void;
}

export const TagFilterBar: React.FC<TagFilterBarProps> = memo(({
  tags,
  selectedTagIds,
  onToggleTag,
  onClearFilters,
}) => {
  const styles = useThemedStyles(makeStyles);
  const hasActiveFilters = selectedTagIds.length > 0;

  if (tags.length === 0) return null;

  return (
    <View style={styles.wrapper} accessibilityRole="toolbar" accessibilityLabel="Tag filters">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {hasActiveFilters && (
          <TouchableOpacity
            style={styles.clearChip}
            onPress={onClearFilters}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Clear all tag filters"
          >
            <Text style={styles.clearText}>Clear</Text>
          </TouchableOpacity>
        )}
        {tags.map((tag) => (
          <TagBadge
            key={tag.id}
            tag={tag}
            size="medium"
            isActive={selectedTagIds.includes(tag.id)}
            onPress={() => onToggleTag(tag.id)}
          />
        ))}
      </ScrollView>
    </View>
  );
});

TagFilterBar.displayName = 'TagFilterBar';

const makeStyles = ({ semantic }: ThemeContextValue) => StyleSheet.create({
  wrapper: {
    paddingVertical: 4,
  },
  scrollContent: {
    paddingHorizontal: 16,
    gap: 8,
    alignItems: 'center',
  },
  clearChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: semantic.interactive.danger,
  },
  clearText: {
    fontSize: 13,
    fontWeight: '500',
    color: semantic.interactive.danger,
  },
});
